// ABOUTME: Component that exports applications dashboard data to CSV format
// ABOUTME: Fetches ALL available data for export, not just visible rows

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/pro-regular-svg-icons';
import { Button, useBreakpointValue, useToast } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { shallowEqual, useSelector } from 'react-redux';

import { IconButtonWithTooltip } from 'shared-components';
import { useSession } from 'shared-components/authentication';

import { ApplicationState } from '../../../store';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { parseApplicationsToCsvString } from '../../../utilities/exports/applications';
import { TableRow } from '../../organisms/ApplyDataGrid/table/types';
import { getAllApplicationsForExport } from './getAllApplicationsForExport';
import { getAuth, getIntakeId, getIntakeRange } from '../../../store/selectors';
import { IIntakeIdWithDates } from 'api-paths-cas';
import { AuthState } from '../../../store/auth/types';

export const ExportApplicationsButton = () => {
  const [exportCsvString, setExportCsvString] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const csvLink = useRef<any>(null);
  const toast = useToast();
  const sessionContext = useSession();

  const userRole = useSelector(({ auth }: ApplicationState) => auth.role);
  const selectedRows = useSelector(({ offersDashboard }: ApplicationState) => offersDashboard.selectedRows || []);
  const allSelected = useSelector(({ offersDashboard }: ApplicationState) => offersDashboard.allSelected || false);
  const dashboardRows = useSelector(
    ({ offersDashboard }: ApplicationState) => offersDashboard.dashboardRows || [],
    shallowEqual,
  );
  const currentTab = useSelector(({ offersDashboard }: ApplicationState) => offersDashboard.currentTab);

  // Get auth and intake information for fetching all data
  const auth: AuthState = useSelector((state: ApplicationState) => getAuth(state), shallowEqual);
  const intakeId: string = useSelector((state: ApplicationState) => getIntakeId(state));
  const intakeRange = useSelector((state: ApplicationState) => getIntakeRange(state, intakeId!)) as any as
    | IIntakeIdWithDates
    | undefined;

  const handleExport = async () => {
    setIsLoading(true);

    try {
      let exportApplications: TableRow[] = [];

      // If user selected specific rows, use only those from the current view
      if (selectedRows.length > 0 && !allSelected) {
        exportApplications = dashboardRows.filter(row => {
          const rowId = row.offerId || row.key?.toString() || '';
          return selectedRows.indexOf(rowId) > -1;
        });
      } else {
        // Fetch ALL data for export (not just visible rows)
        const idToken = sessionContext?.getIdToken ? await sessionContext.getIdToken() : '';
        if (!idToken) {
          toast({
            title: 'Export failed',
            description: 'Unable to authenticate. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        exportApplications = await getAllApplicationsForExport({ auth, intakeId, intakeRange, idToken });

        if (!exportApplications || exportApplications.length === 0) {
          toast({
            title: 'No data to export',
            description: 'No applications found matching the current filters.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      const csvString = parseApplicationsToCsvString(exportApplications);
      setExportCsvString(csvString);

      toast({
        title: 'Export successful',
        description: `Exporting ${exportApplications.length} applications to CSV.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: 'Export failed',
        description: 'An error occurred while exporting data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (exportCsvString) {
      // https://github.com/react-csv/react-csv/issues/221
      setTimeout(() => {
        csvLink?.current?.link.click();
        setExportCsvString('');
      });
    }
  }, [exportCsvString]);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const buttonLabel = selectedRows.length > 0 && !allSelected ? 'Export' : 'Export all';

  return (
    <>
      {hasPerm(userRole, P.STUDENTS_EXPORTS) && (
        <>
          {isMobile ? (
            <IconButtonWithTooltip
              label={buttonLabel}
              iconProps={{ icon: faShare }}
              buttonProps={{
                size: 'md',
                variant: 'ghost',
                isLoading,
              }}
              onClick={handleExport}
            />
          ) : (
            <Button
              size="sm"
              variant="ghost"
              flexShrink="0"
              onClick={handleExport}
              isLoading={isLoading}
              loadingText="Exporting..."
              leftIcon={<FontAwesomeIcon className="utility-icon" icon={faShare} />}
            >
              {buttonLabel}
            </Button>
          )}

          <CSVLink
            data={exportCsvString}
            filename={`applications-${currentTab}-${new Date().toDateString().split(' ').join('-')}.csv`}
            target="_blank"
            ref={csvLink}
            style={{ display: 'none' }}
          >
            Download
          </CSVLink>
        </>
      )}
    </>
  );
};
